import { DEFAULT_VISIT_RULES, NEEDS, NEED_KEYS, type NeedKey, type VisitRules } from '@london-life/contracts';
import { Input } from '@london-life/ui/components/input';
import { Label } from '@london-life/ui/components/label';
import { PageHeader } from '@london-life/ui/admin/page-header';
import { ErrorNotice } from '@london-life/ui/feedback/error-notice';
import { useAdmin } from '../../lib/admin-context';
import { useContent } from './use-content';
import { ContentBar } from './versions';
import { wholeNumber, within } from './form';

// the limits the game accepts (contracts visits.ts)
const FIELDS: {
  key: 'knockWaitSeconds' | 'knockAgainMinutes' | 'inviteHours' | 'maxGuests';
  label: string;
  min: number;
  max: number;
  hint: string;
}[] = [
  {
    key: 'knockWaitSeconds',
    label: 'Knock waits (seconds)',
    min: 15,
    max: 900,
    hint: 'Then nobody answered, and the guest goes home.',
  },
  {
    key: 'knockAgainMinutes',
    label: 'Knock again after (minutes)',
    min: 0,
    max: 1_440,
    hint: 'After being turned away or no answer, at the same door.',
  },
  {
    key: 'inviteHours',
    label: 'An invite lasts (hours)',
    min: 1,
    max: 168,
    hint: '"Invite over" from a chat: they walk straight in until then.',
  },
  { key: 'maxGuests', label: 'Guests at once', min: 1, max: 20, hint: 'At the door and inside together.' },
];

// Visits: the rules for dropping by a neighbour's home (Lagos Life's knock and "Invite over"), and what being let
// in does for the guest and the host. Part of the content draft, published with it.
export function VisitsPage() {
  const { can } = useAdmin();
  const editor = useContent();
  const working = editor.working;
  const readOnly = !can('content.write');
  const rules = working?.visits ?? DEFAULT_VISIT_RULES;
  // content made before visits were content gets the starting rules the first time it is changed
  const change = (fn: (rules: VisitRules) => void) =>
    editor.edit((content) => {
      content.visits ??= structuredClone(DEFAULT_VISIT_RULES);
      fn(content.visits);
    });
  const welcome = (who: 'guest' | 'host', title: string) => (
    <>
      <p className="content-mini-title">{title} (−100 to 100)</p>
      <div className="content-needs">
        {NEED_KEYS.map((need: NeedKey) => (
          <label key={need}>
            <span>
              {NEEDS[need].icon} {NEEDS[need].label}
            </span>
            <Input
              type="number"
              min={-100}
              max={100}
              aria-label={`${title}: ${NEEDS[need].label}`}
              value={rules.welcome[who][need] ?? ''}
              onChange={(e) =>
                change((next) => {
                  const value = within(wholeNumber(e.target.value, 0), -100, 100);
                  if (value) next.welcome[who][need] = value;
                  else delete next.welcome[who][need];
                })
              }
            />
          </label>
        ))}
      </div>
    </>
  );
  return (
    <section>
      <PageHeader
        title="Visits"
        description="Dropping by a neighbour's home: how long a knock waits, how soon a player may knock again, how long an invite lasts, how many guests at once, and what being let in does. Changes are a draft until they are published."
      />
      <ContentBar editor={editor} />
      {editor.failure && <ErrorNotice error={editor.failure} onRetry={() => void editor.reload()} />}
      {!working && !editor.failure && <p className="admin-muted">Loading…</p>}
      {working && (
        <div className="content-editor">
          <fieldset className="admin-panel content-section" disabled={readOnly} aria-label="The rules">
            <h2 className="content-subtitle">The rules</h2>
            <div className="content-grid">
              {FIELDS.map((field) => (
                <div className="admin-field" key={field.key}>
                  <Label htmlFor={`visits-${field.key}`}>{field.label}</Label>
                  <Input
                    id={`visits-${field.key}`}
                    type="number"
                    min={field.min}
                    max={field.max}
                    value={rules[field.key]}
                    onChange={(e) =>
                      change(
                        (next) =>
                          void (next[field.key] = within(wholeNumber(e.target.value, field.min), field.min, field.max)),
                      )
                    }
                  />
                  <small className="admin-muted">{field.hint}</small>
                </div>
              ))}
            </div>
          </fieldset>
          <fieldset className="admin-panel content-section" disabled={readOnly} aria-label="Being let in">
            <h2 className="content-subtitle">Being let in</h2>
            {welcome('guest', 'What it does for the guest')}
            {welcome('host', 'What it does for the host')}
          </fieldset>
        </div>
      )}
    </section>
  );
}
